import { useMutation } from '@tanstack/react-query';

import { assignUsers } from '@cfa/pathway-fe-shared/xp';

import {
  invalidateAllPlanQueries,
  invalidateCertificateQueries,
} from '@/utils/queryInvalidation';

import { useAmplitudeTrack } from '@/amplitude/useAmplitude';
import { trackFirstAssignDurationIfMarked } from '@/routes/TrainingPlans/planBuildTimer';

interface AssignTeamMembersParams {
  planId: string;
  userIds: string[];
  dueDate?: string;
  isFirstAssignment?: boolean;
}

/**
 * Assigns users to a plan via the consolidated `assignUsers` endpoint.
 * The backend handles reassignment, recertification resets, and due-date updates.
 *
 * After the mutation resolves, invalidates common plan/assignment queries
 * and certificate queries. Does not show toasts — consumers handle their own.
 */
export const useAssignTeamMembers = () => {
  const track = useAmplitudeTrack();

  const mutation = useMutation({
    mutationFn: async ({
      planId,
      userIds,
      dueDate,
    }: AssignTeamMembersParams) => {
      if (userIds.length === 0) return;

      await assignUsers({
        path: { planId },
        body: { userIds, ...(dueDate !== undefined && { dueDate }) },
        throwOnError: true,
      });
    },
    onSuccess: async (_data, { planId, userIds, isFirstAssignment }) => {
      if (isFirstAssignment && userIds.length > 0) {
        trackFirstAssignDurationIfMarked(track, planId);
      }

      await Promise.all([
        invalidateAllPlanQueries(planId),
        invalidateCertificateQueries(planId, userIds),
      ]);
    },
  });

  return {
    assignTeamMembers: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
};
